import { type FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { isMenuSubVisible, isPathActive } from '../../../authz/menu-visibility';
import type { IPermissionSnapshot } from '../../../authz/types';
import { AngleRightSVG } from '../../../components/reusable/svgs';
import type { IMenuItem } from '../../utils';

interface MenuItemProps extends IMenuItem {
	snapshot: IPermissionSnapshot;
	hasElection?: boolean;
	onRequestElection?: () => void;
}

export const MenuItem: FC<MenuItemProps> = ({
	icon,
	title,
	pageUrl,
	action,
	closeAction,
	sub,
	requiresElection,
	snapshot,
	hasElection = true,
	onRequestElection,
}) => {
	const navigate = useNavigate();
	const location = useLocation();

	const filteredSub = useMemo(() => sub?.filter((i) => isMenuSubVisible(i, snapshot)) ?? [], [sub, snapshot]);

	const subPath = (subItem: { url: string }) => (subItem.url ? `${pageUrl}/${subItem.url}` : pageUrl);

	// Open the group when the route is inside it. Lazy-init covers the first render;
	// the effect covers navigating in from elsewhere. Without this the group collapses
	// on every remount, hiding the siblings of the page you are actually on.
	const hasActiveChild = filteredSub.some((i) => isPathActive(subPath(i), location.pathname));
	const [isSub, setIsSub] = useState<boolean>(hasActiveChild);

	useEffect(() => {
		if (hasActiveChild) setIsSub(true);
	}, [hasActiveChild]);

	const needsElectionPrompt = (item: { requiresElection?: boolean }) => !!item.requiresElection && !hasElection;
	const itemNeedsElection = needsElectionPrompt({ requiresElection });

	const onClick = () => {
		if (filteredSub.length > 0) {
			setIsSub(!isSub);
			return;
		}
		if (itemNeedsElection && onRequestElection) {
			onRequestElection();
			closeAction?.();
			return;
		}
		if (typeof action === 'function') {
			action();
		} else {
			navigate(pageUrl);
		}
		closeAction?.();
	};

	const onSubClick = (subItem: { url: string; requiresElection?: boolean }) => {
		if (needsElectionPrompt(subItem) && onRequestElection) {
			onRequestElection();
			closeAction?.();
			return;
		}
		navigate(subPath(subItem));
		closeAction?.();
	};

	const isMenu = isPathActive(pageUrl, location.pathname);

	const activeClass = isMenu ? 'active' : '';

	const showElectionBadge = itemNeedsElection && filteredSub.length === 0;

	return (
		<li
			className={`${activeClass} ${itemNeedsElection ? 'menu-item--requires-election' : ''}`.trim()}
			data-tooltip={title}
		>
			<div
				className='menu-header'
				onClick={onClick}
				role='button'
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onClick();
					}
				}}
				aria-current={isMenu ? 'page' : undefined}
				aria-expanded={filteredSub.length > 0 ? isSub : undefined}
				aria-label={showElectionBadge ? `${title} (select an election first)` : title}
			>
				<div className='svg-container'>{icon}</div>
				<p>{title}</p>
				{showElectionBadge && (
					<span
						className='menu-election-dot'
						aria-hidden='true'
						title='Select an election to access'
					/>
				)}
				{filteredSub.length > 0 && (
					<div className={`hw-mx ml-auto menu-chevron ${isSub ? 'menu-chevron--open' : ''}`}>
						<AngleRightSVG />
					</div>
				)}
			</div>

			{filteredSub.length > 0 && (
				<div
					className='sub-menu-container'
					style={{
						maxHeight: isSub ? `${filteredSub.length * 36 + 12}px` : '0',
						opacity: isSub ? 1 : 0,
						marginTop: isSub ? '4px' : '0',
						marginBottom: isSub ? '6px' : '0',
					}}
				>
					{filteredSub.map((i, index) => {
						const subNeedsElection = needsElectionPrompt(i);
						return (
							<div
								key={index}
								className={`sub-menu-item ${isPathActive(subPath(i), location.pathname) ? 'active' : ''} ${
									subNeedsElection ? 'sub-menu-item--requires-election' : ''
								}`.trim()}
								onClick={() => onSubClick(i)}
								role='button'
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										onSubClick(i);
									}
								}}
							>
								<span>{i.title}</span>
								{subNeedsElection && (
									<span
										className='menu-election-dot menu-election-dot--sub'
										aria-hidden='true'
										title='Select an election to access'
									/>
								)}
							</div>
						);
					})}
				</div>
			)}
		</li>
	);
};
